import {
  Box,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PropsWithChildren, useState } from "react";
import dayjs from "dayjs";
import { Deal, DealType } from "src/business/deal";
import {
  colorSurface,
  colorBorder,
  colorTextPrimary,
  colorTextMuted,
  colorTextSecondary,
  shadowCard,
  radiusCard,
  iconBgPurple,
  iconBgInfo,
  iconColorPurple,
  iconColorInfo,
  radiusInput,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";

interface Props {
  deal: Deal;
  canUpdate: boolean;
  onToggle: (deal: Deal) => void;
  onEdit: (deal: Deal) => void;
}

const DetailChipWrapper = ({ children }: PropsWithChildren) => (
  <Box
    sx={{
      px: 0.5,
      py: 0.2,
      borderColor: colorBorder,
      borderRadius: radiusInput,
      borderStyle: "solid",
      borderWidth: "1px",
    }}
  >
    {children}
  </Box>
);

export const DealListItem = ({ deal, canUpdate, onToggle, onEdit }: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isEvent = deal.type === DealType.EVENT;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleEdit = () => {
    handleMenuClose();
    onEdit(deal);
  };

  return (
    <Box
      sx={{
        backgroundColor: colorSurface,
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        px: { xs: 2, sm: 3 },
        py: 2,
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "0px 4px 6px rgba(0,0,0,0.07)" },
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Icon + content */}
        <Grid item xs={12} sm>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: isEvent ? iconBgPurple : iconBgInfo,
                border: "1px solid #FED7AA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isEvent ? iconColorPurple : iconColorInfo,
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {isEvent ? (
                <EventNoteOutlinedIcon sx={{ fontSize: 18 }} />
              ) : (
                <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, fontSize: "14px", color: colorTextPrimary }}
                >
                  {deal.content.split("\n")[0] || deal.content}
                </Typography>
                <Chip
                  label={
                    isEvent
                      ? t("deals.event").toUpperCase()
                      : t("deals.deal").toUpperCase()
                  }
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "10px",
                    fontWeight: 600,
                    backgroundColor: isEvent ? iconBgPurple : iconBgInfo,
                    color: isEvent ? iconColorPurple : iconColorInfo,
                    border: "none",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
                <Chip
                  label={deal.active ? t("deals.active") : t("deals.inactive")}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "10px",
                    fontWeight: 600,
                    backgroundColor: deal.active ? "#F0FDF4" : "#F3F4F6",
                    color: deal.active ? "#15803D" : "#6B7280",
                    border: `1px solid ${deal.active ? "#BBF7D0" : "#E5E7EB"}`,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: "13px",
                  color: colorTextSecondary,
                  mb: 0.75,
                  lineHeight: 1.4,
                }}
                noWrap
              >
                {deal.content}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  mb: 0.5,
                }}
              >
                <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: colorTextMuted }} />
                {deal.days.slice(0, 3).map((day, index) => (
                  <DetailChipWrapper key={`${deal.id}-day-${index}`}>
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: "600", color: colorTextMuted }}
                    >
                      {dayjs(day).format("DD MMM, YYYY")}
                    </Typography>
                  </DetailChipWrapper>
                ))}
                {deal.days.length > 3 && (
                  <DetailChipWrapper>
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: "600", color: colorTextMuted }}
                    >
                      + {deal.days.length - 3}
                    </Typography>
                  </DetailChipWrapper>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 12, color: colorTextMuted }} />
                {deal.timeRanges.slice(0, 3).map((timeRange, index) => (
                  <DetailChipWrapper key={`${deal.id}-timeRange-${index}`}>
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: "600", color: colorTextMuted }}
                    >
                      {timeRange}
                    </Typography>
                  </DetailChipWrapper>
                ))}
                {deal.timeRanges.length > 3 && (
                  <DetailChipWrapper>
                    <Typography
                      sx={{ fontSize: "12px", fontWeight: "600", color: colorTextMuted }}
                    >
                      + {deal.timeRanges.length - 3}
                    </Typography>
                  </DetailChipWrapper>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Actions */}
        {canUpdate && (
        <Grid item xs={12} sm="auto">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              justifyContent: { xs: "flex-end", sm: "flex-start" },
            }}
          >
            <Switch
              checked={deal.active}
              size="small"
              onChange={(e) => {
                e.stopPropagation();
                onToggle({ ...deal, active: !deal.active });
              }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#10B981" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#10B981",
                },
              }}
            />
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ color: colorTextMuted }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                sx: { boxShadow: shadowCard, border: `1px solid ${colorBorder}` },
              }}
            >
              <MenuItem onClick={handleEdit} sx={{ fontSize: "14px" }}>
                {t("deals.edit")}
              </MenuItem>
            </Menu>
          </Box>
        </Grid>
        )}
      </Grid>
    </Box>
  );
};
